import type {ContentPart, ImagePart} from '../store';
import type {ComposerFile} from './file';
import type {SelectedRun} from './run';

export interface AssemblePromptPartsInput {
  // Attached run chips (ambient context), prepended before the message body.
  runs: SelectedRun[];
  // The typed message; starts with a text part (empty when it leads with a mention).
  body: ContentPart[];
  // File attachments (images today), appended after the body.
  files: ComposerFile[];
}

/**
 * assemblePromptParts orders a user prompt from a draft's pieces. Run chips
 * lead as ambient context, then the message body, then files after a text
 * anchor so they survive the `getVisiblePromptParts` slice (everything before
 * the first text part is treated as ambient and dropped from display).
 */
export function assemblePromptParts({
  runs,
  body,
  files,
}: AssemblePromptPartsInput): ContentPart[] {
  const runParts: ContentPart[] = runs.map(run => ({
    type: 'wandb-run',
    entityName: run.entityName,
    projectName: run.projectName,
    runName: run.name,
    runDisplayName: run.displayName,
    runDisplayNameStatus: 'resolved',
  }));

  const parts: ContentPart[] = [...runParts, ...body];
  if (files.length > 0) {
    if (!parts.some(part => part.type === 'text')) {
      parts.push({type: 'text', text: '', status: 'complete'});
    }

    // Drop the composer-only `id`, leaving the store/wire `ImagePart`.
    for (const {id: _id, ...imagePart} of files) {
      parts.push(imagePart);
    }
  }

  return parts;
}

export interface DisassembledPrompt {
  runs: SelectedRun[];
  body: ContentPart[];
  images: ImagePart[];
}

/**
 * disassemblePromptParts splits assembled parts back into a draft's pieces, the
 * inverse of `assemblePromptParts`. Leading run chips become `runs`, images are
 * returned for the caller to restore as files, and the message from the first
 * text part on becomes the `body`.
 *
 * NOTE: Ambient context (e.g. page-state) in the leading region is dropped.
 */
export function disassemblePromptParts(
  parts: ContentPart[]
): DisassembledPrompt {
  const firstTextIndex = parts.findIndex(part => part.type === 'text');
  if (firstTextIndex === -1) {
    return {
      runs: parts
        .filter(part => part.type === 'wandb-run')
        .map(runPartToSelectedRun),
      body: [],
      images: [],
    };
  }

  // The user's typed message begins at the first text part; everything before
  // it is ambient context, of which only run chips are kept (as `runs`).
  const leading = parts.slice(0, firstTextIndex);
  const rest = parts.slice(firstTextIndex);

  const runs = leading
    .filter(part => part.type === 'wandb-run')
    .map(runPartToSelectedRun);

  const body: ContentPart[] = [];
  const images: ImagePart[] = [];
  for (const part of rest) {
    if (isAttachmentPart(part)) {
      images.push(part);
    } else {
      body.push(part);
    }
  }

  return {runs, body, images};
}

function runPartToSelectedRun(
  part: Extract<ContentPart, {type: 'wandb-run'}>
): SelectedRun {
  return {
    name: part.runName,
    displayName: part.runDisplayName ?? part.runName,
    entityName: part.entityName,
    projectName: part.projectName,
  };
}

/**
 * isAttachmentPart reports whether a part round-trips through the composer as a
 * file rather than inline body content.
 */
function isAttachmentPart(part: ContentPart): part is ImagePart {
  switch (part.type) {
    case 'image':
      return true;
    case 'text':
    case 'thinking':
    case 'tool-call':
    case 'wandb-entity':
    case 'wandb-project':
    case 'wandb-run':
    case 'wandb-call':
    case 'wandb-conversation':
    case 'wandb-workspace-panel':
    case 'wandb-web-page-state':
    case 'wandb-artifact-collection':
    case 'unknown':
      return false;
    default: {
      // Force a compile error when a new ContentPart variant is added, so its
      // composer round-trip is classified here rather than silently dropped.
      const _exhaustive: never = part;
      return _exhaustive;
    }
  }
}
